import PageLoader from "@/components/loader/PageLoader";
import LocaleSelector from "@/components/selectors/LocaleSelector";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import {
  faUserCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store";
import { setAvatarUrl } from "@/store/modules/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";

const Navbar = () => {
  const { t } = useTranslation();
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const dispatch = useDispatch();
  const ownerAvatar = useSelector((state: RootState) => state.avatar.url);

  useEffect(() => {
    const fetchOwnerAvatar = async () => {
      try {
        if (!loading && axiosInstance && user?.owner) {
          const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
            import.meta.env.VITE_APP_REDBIRDPOSBE_OWNER
          }`;
          if (!apiUrl) {
            console.error("API URL is not defined.");
            return;
          }
          const response = await axiosInstance.get(
            `${apiUrl}${user.owner.id}/`,
          );
          dispatch(setAvatarUrl(response.data.avatar));
        }
      } catch (error) {
        console.error("Error fetching owner avatar:", error);
      }
    };
    fetchOwnerAvatar();
  }, [axiosInstance, loading, user, dispatch]);

  const handleLogout = () => {
    logout();
    window.location.href = "/";
  };

  const styles: Record<string, React.CSSProperties> = {
    navbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#F8F8F8",
      padding: "16px",
      boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
      position: "relative",
      zIndex: 45,
    },
    avatar: {
      height: "40px",
      width: "40px",
      borderRadius: "50%",
      marginRight: "8px",
    },
  };

  return (
    <PageLoader isFetching={isFetching}>
      <nav style={styles.navbar}>
        <div style={{ display: "flex", alignItems: "center" }}></div>

        <div style={{ display: "flex", alignItems: "center" }}>
          <div className="flex">
            <LocaleSelector className="px-2" />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-md border border-transparent px-2 py-1 hover:border-border hover:bg-accent/50 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                {ownerAvatar ? (
                  <img
                    src={ownerAvatar}
                    alt="Owner Avatar"
                    style={styles.avatar}
                  />
                ) : (
                  <FontAwesomeIcon icon={faUserCircle} style={styles.avatar} />
                )}
                <span className="text-xl font-bold">
                  {user?.username || "User"}
                </span>
                <ChevronDown className="size-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className="w-40" 
              style={{ zIndex: 999 }}
              sideOffset={8}
            >
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                {t("back.management.navbar.dropdown.profile")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                {t("back.management.navbar.dropdown.settings")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout}>
                {t("back.management.navbar.dropdown.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
    </PageLoader>
  );
};

export default Navbar;
